/** Sprint 5-d PR-4 · 左側 session 列表（pinned/today/yesterday/older 分組 + kebab 操作 + CostMeter 底）。 */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, IconButton, List, ListItemButton, ListItemText, Menu, MenuItem, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PushPinIcon from '@mui/icons-material/PushPin';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import ViewSidebarIcon from '@mui/icons-material/ViewSidebar';
import HistoryIcon from '@mui/icons-material/History';

import { ChatSession } from '../services/aiChatService';
import type { SystemFunction } from '../../../../types';

interface Props {
  sessions: ChatSession[];
  currentId: number | null;
  functions?: SystemFunction[];   // §5.5 對話列表範圍圖示對照
  footer?: React.ReactNode;   // 底部 profile 區塊（Claude-style·成本移入其中）
  collapsed?: boolean;            // RWD D19·桌機收成 52px 窄邊
  onToggleCollapse?: () => void;
  onNew: () => void;
  onSelect: (id: number) => void;
  onRename: (s: ChatSession) => void;
  onPin: (s: ChatSession) => void;
  onArchive: (s: ChatSession) => void;
  onDelete: (s: ChatSession) => void;
}

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

const SessionSidebar: React.FC<Props> = (p) => {
  const { t } = useTranslation();
  const [menuFor, setMenuFor] = useState<ChatSession | null>(null);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const groups = useMemo(() => {
    const today = startOfDay(new Date());
    const yesterday = today - 864e5;
    const g: { pinned: ChatSession[]; today: ChatSession[]; yesterday: ChatSession[]; older: ChatSession[] } =
      { pinned: [], today: [], yesterday: [], older: [] };
    for (const s of p.sessions) {
      if (s.is_pinned) { g.pinned.push(s); continue; }
      const ts = startOfDay(new Date(s.last_message_at || s.created_at));
      if (ts >= today) g.today.push(s);
      else if (ts >= yesterday) g.yesterday.push(s);
      else g.older.push(s);
    }
    return g;
  }, [p.sessions]);

  const openMenu = (s: ChatSession, el: HTMLElement) => { setMenuFor(s); setAnchor(el); };
  const closeMenu = () => { setMenuFor(null); setAnchor(null); };

  // §5.5 範圍圖示：一般對話（不限範圍）💬 / 範圍對話取該功能 func_icon
  const scopeIcon = (s: ChatSession): string => {
    if (!s.session_scope) return '💬';
    const f = (p.functions || []).find((x) => x.func_code === s.session_scope);
    return f?.func_icon || '▧';
  };

  const renderGroup = (label: string, rows: ChatSession[], pinned = false) => rows.length > 0 && (
    <Box key={label}>
      <Typography variant="caption" color="text.secondary" sx={{ px: 2, pt: 1, display: 'block' }}>{label}</Typography>
      <List dense disablePadding>
        {rows.map((s) => (
          <ListItemButton key={s.id} selected={s.id === p.currentId} onClick={() => p.onSelect(s.id)}
            sx={{ pr: 5 }}>
            {pinned && <PushPinIcon fontSize="inherit" sx={{ mr: 0.5, color: 'text.disabled' }} />}
            <Box component="span" sx={{ mr: 0.5, fontSize: 14, flexShrink: 0 }} aria-hidden>{scopeIcon(s)}</Box>
            <ListItemText primary={s.title || t('chat.untitled', '（未命名對話）')}
              primaryTypographyProps={{ noWrap: true, variant: 'body2' }} />
            <IconButton size="small" sx={{ position: 'absolute', right: 4 }}
              onClick={(e) => { e.stopPropagation(); openMenu(s, e.currentTarget); }}>
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </ListItemButton>
        ))}
      </List>
    </Box>
  );

  const reduceMotion = typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const widthTransition = reduceMotion ? undefined : 'width .2s';

  // RWD D19 · 收折窄邊（52px）：展開鈕 / ＋新對話 / 🕘展開 / …留白 / 底部頭像
  if (p.collapsed) {
    return (
      <Box sx={{ width: 52, borderRight: 1, borderColor: 'divider', display: 'flex',
                 flexDirection: 'column', alignItems: 'center', height: '100%', transition: widthTransition }}>
        <Tooltip title={t('chat.sidebar.expand', '展開對話列表')} placement="right">
          <IconButton size="small" sx={{ mt: 1 }} onClick={p.onToggleCollapse}><ViewSidebarIcon /></IconButton>
        </Tooltip>
        <Tooltip title={t('chat.newSession', '新對話')} placement="right">
          <IconButton size="small" sx={{ mt: 1 }} onClick={p.onNew}><AddIcon /></IconButton>
        </Tooltip>
        <Tooltip title={t('chat.sidebar.history', '對話紀錄')} placement="right">
          <IconButton size="small" sx={{ mt: 1 }} onClick={p.onToggleCollapse}><HistoryIcon /></IconButton>
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        <Box sx={{ width: '100%', overflow: 'hidden' }}>
          {React.isValidElement(p.footer)
            ? React.cloneElement(p.footer as React.ReactElement<{ compact?: boolean }>, { compact: true })
            : p.footer}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ width: 260, borderRight: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', height: '100%', transition: widthTransition }}>
      <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button fullWidth variant="contained" startIcon={<AddIcon />} onClick={p.onNew}>
          {t('chat.newSession', '新對話')}
        </Button>
        {p.onToggleCollapse && (
          <Tooltip title={t('chat.sidebar.collapse', '收起對話列表')}>
            <IconButton size="small" onClick={p.onToggleCollapse}><MenuOpenIcon /></IconButton>
          </Tooltip>
        )}
      </Box>
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {renderGroup(t('chat.pinned', '已釘選'), groups.pinned, true)}
        {renderGroup(t('chat.today', '今日'), groups.today)}
        {renderGroup(t('chat.yesterday', '昨日'), groups.yesterday)}
        {renderGroup(t('chat.older', '更早'), groups.older)}
      </Box>
      {p.footer}

      <Menu anchorEl={anchor} open={Boolean(menuFor)} onClose={closeMenu}>
        <MenuItem onClick={() => { if (menuFor) p.onRename(menuFor); closeMenu(); }}>{t('chat.rename', '重新命名')}</MenuItem>
        <MenuItem onClick={() => { if (menuFor) p.onPin(menuFor); closeMenu(); }}>
          {menuFor?.is_pinned ? t('chat.unpin', '取消釘選') : t('chat.pin', '釘選')}</MenuItem>
        <MenuItem onClick={() => { if (menuFor) p.onArchive(menuFor); closeMenu(); }}>
          {menuFor?.is_archived ? t('chat.unarchive', '取消封存') : t('chat.archive', '封存')}</MenuItem>
        <MenuItem onClick={() => { if (menuFor) p.onDelete(menuFor); closeMenu(); }} sx={{ color: 'error.main' }}>
          {t('chat.delete', '刪除')}</MenuItem>
      </Menu>
    </Box>
  );
};

export default SessionSidebar;
